/**
 * Copyright 2026 Salesforce, Inc.
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
import type { ReactElement } from 'react';
import { useTranslation } from 'react-i18next';
import { usePageDesignerMode } from '@salesforce/storefront-next-runtime/design/react/core';
import { Component } from '@/lib/decorators/component';
import { AttributeDefinition } from '@/lib/decorators/attribute-definition';
import { RegionDefinition } from '@/lib/decorators';
import { Link } from '@/components/link';
import { ResponsiveBannerImage } from '@/components/responsive-banner-image';
import { cn } from '@/lib/utils';
import type { Image } from '@/types';

type ImageCardStyle = 'tile' | 'category';

const IMAGE_CARD_STYLES: Record<ImageCardStyle, { widths: string[]; imageClass: string }> = {
    // "¡Moda a la vista!": 3 per row, 16px radius with a soft shadow.
    tile: { widths: ['33vw'], imageClass: 'rounded-2xl shadow-[0_2px_12px_rgb(0_0_0/0.07)]' },
    // "Categorías": 4.5 per view in the Carousel, 8px radius, no shadow.
    category: { widths: ['22vw'], imageClass: 'rounded-lg' },
};

@Component('imageCard', {
    name: 'Image Card',
    group: 'priceshoes_content',
    description:
        'Card with a 324px image and an optional title and text below it; the whole card links to the configured URL. Used in the Image Card Grid (Moda a la vista) and the Carousel (Categorías).',
})
@RegionDefinition([])
// oxlint-disable-next-line react/only-export-components -- oxlint flags the co-exported Page Designer metadata class; eslint-plugin-react-refresh does not
export class ImageCardMetadata {
    @AttributeDefinition({ name: 'Image', type: 'image', required: true })
    image!: string;

    @AttributeDefinition({ name: 'Image Alt Text', type: 'string', required: false })
    imageAlt?: string;

    @AttributeDefinition({ name: 'Link', type: 'url', required: true, description: 'Where the card redirects' })
    link!: string;

    @AttributeDefinition({
        name: 'Title',
        type: 'string',
        required: false,
        description: 'Shown below the image, e.g. the category name "Calzados"',
    })
    title?: string;

    @AttributeDefinition({
        name: 'Text',
        type: 'string',
        required: false,
        description: 'Shown below the title, e.g. "40% OFF en productos seleccionados"',
    })
    text?: string;

    @AttributeDefinition({
        id: 'style',
        name: 'Style',
        description:
            'Default: tile (also when empty). tile: 16px rounded corners with shadow (Moda a la vista). category: 8px rounded corners, no shadow (Categorías).',
        type: 'enum',
        // Literal list: the cartridge generator reads decorators statically. Keep in sync with ImageCardStyle.
        values: ['tile', 'category'],
        defaultValue: 'tile',
    })
    style?: string;
}

interface ImageCardProps {
    image?: Image;
    imageAlt?: string;
    link?: string;
    title?: string;
    text?: string;
    /** 'tile' (default) or 'category'. PD enums arrive as strings. */
    style?: string;
    className?: string;
}

/**
 * Image card: fixed 324px image (Figma) with an optional title and text below.
 * The whole card is a link.
 */
export default function ImageCard({
    image,
    imageAlt,
    link,
    title,
    text,
    style,
    className,
}: ImageCardProps): ReactElement | null {
    const { t } = useTranslation('common');
    const { isDesignMode } = usePageDesignerMode();
    const styles = IMAGE_CARD_STYLES[style === 'category' ? 'category' : 'tile'];
    const heading = title?.trim();
    const description = text?.trim();

    // Authoring affordance only: on the live storefront a card without image renders nothing.
    if (!image?.url && !isDesignMode) return null;

    const content = (
        <>
            <div className={cn('relative h-[324px] w-full overflow-hidden bg-ps-gray-200', styles.imageClass)}>
                {image?.url ? (
                    <ResponsiveBannerImage
                        image={image}
                        alt={imageAlt}
                        widths={styles.widths}
                        className="absolute inset-0 size-full transition-opacity group-hover:opacity-90"
                    />
                ) : (
                    <span className="flex size-full items-center justify-center text-ps-gray-500">
                        {t('imageCard.emptyLabel')}
                    </span>
                )}
            </div>
            {(heading || description) && (
                <div className="flex flex-col gap-1 pl-3 text-foreground">
                    {heading && <p className="text-lg font-bold leading-7">{heading}</p>}
                    {description && <p className="text-base leading-6">{description}</p>}
                </div>
            )}
        </>
    );

    const cardClass = cn('group flex w-full flex-col gap-2', className);

    return link ? (
        <Link
            to={link}
            data-slot="image-card"
            // With a visible title the link already has a name; otherwise use the alt text.
            aria-label={heading ? undefined : imageAlt || undefined}
            className={cn(
                cardClass,
                'rounded-lg focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ps-focus'
            )}>
            {content}
        </Link>
    ) : (
        <div data-slot="image-card" className={cardClass}>
            {content}
        </div>
    );
}
